import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';

/**
 * "Manage Subscription" button with its upward-opening Update / Pause menu. Presentational: the
 * host decides what is offered (see pauseViewState) and handles the outputs. Items are shown only
 * when they can be used, never disabled (CWS-1734, CWS-1737). Shared by the
 * Customers → Subscriptions cards and the lane subscription popups.
 *
 * @selector app-manage-subscription-menu
 * @input showUpdate - Update is offered (and usable).
 * @input showPause - Pause is offered (and usable).
 * @input showCancelScheduled - Replace Pause with "Cancel scheduled pause".
 * @input showCancel - Cancel membership is offered (CWS-1780); always the last item.
 * @output update - Update chosen.
 * @output pause - Pause chosen.
 * @output cancelScheduledPause - Cancel scheduled pause chosen.
 * @output cancelMembership - Cancel membership chosen.
 * @input showPaymentMethod - Update Payment method is offered (CWS-1536).
 * @output paymentMethod - Update Payment method chosen.
 * @input showStartFamily - Start Family is offered (CWS-1581).
 * @output startFamily - Start Family chosen.
 * @input label - The trigger text; "Manage Membership" on the Family Plan tab.
 * @input showEditDetails - Edit Details is offered (Family Plan tab).
 * @output editDetails - Edit Details chosen.
 * @input showMoveToOwnNumber - Move to own number is offered (Family Plan tab), before Remove member.
 * @output moveToOwnNumber - Move to own number chosen.
 * @input showRemoveMember - Remove member is offered (Family Plan tab); always the last item.
 * @output removeMember - Remove member chosen.
 */
@Component({
  selector: 'app-manage-subscription-menu',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './manage-subscription-menu.component.html',
  styleUrls: ['./manage-subscription-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ManageSubscriptionMenuComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * Closes the menu on a click or tap outside it. Capture phase: the lane pop-ups stop click
   * propagation on their containers, so a bubbling `document:click` never arrives there.
   */
  private readonly onOutsideClick = (event: Event): void => {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  };

  @Input() showUpdate = false;
  @Input() showPause = false;
  @Input() showCancelScheduled = false;
  @Input() showCancel = false;
  @Input() showPaymentMethod = false;
  @Input() showStartFamily = false;
  @Input() label = 'Manage subscription';
  @Input() showEditDetails = false;
  @Input() showMoveToOwnNumber = false;
  @Input() showRemoveMember = false;

  @Output() update = new EventEmitter<void>();
  @Output() pause = new EventEmitter<void>();
  @Output() cancelScheduledPause = new EventEmitter<void>();
  @Output() cancelMembership = new EventEmitter<void>();
  @Output() paymentMethod = new EventEmitter<void>();
  @Output() startFamily = new EventEmitter<void>();
  @Output() editDetails = new EventEmitter<void>();
  @Output() moveToOwnNumber = new EventEmitter<void>();
  @Output() removeMember = new EventEmitter<void>();

  open = false;

  constructor() {
    const document = inject<Document>(DOCUMENT);
    document.addEventListener('click', this.onOutsideClick, true);
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('click', this.onOutsideClick, true)
    );
  }

  toggle(): void {
    this.open = !this.open;
  }

  choose(output: EventEmitter<void>): void {
    this.open = false;
    output.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open = false;
  }
}
